import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate } from 'react-router-dom'

import { FilterMatchMode, FilterOperator } from 'primereact/api'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTableFilterMeta } from 'primereact/datatable'

import { SearchField } from '../../../../../components/SearchField'
import {
  BooleanBodyTemplate,
  BooleanFilterTemplate,
} from '../../../../../components/tableTemplates'
import { useSearchParam } from '../../../../../hooks'
import { useColumnToggle } from '../../../../../hooks/useColumnToggle'
import { ConfigurableTable } from '../../../../../utils/wrapper'
import { useConfigContext } from '../../../../ui-config'
import { MasterDataDownloadButton } from '../../common/components'
import { CompanyCode } from '../../suppliers'
import { requestDownloadCompanyCode, useAllCompanyCodes } from '../data'
import { MasterDataUpload } from './dialogs/upload'

const defaultFilters: DataTableFilterMeta = {
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  IsStandard: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  'Common.DisplayId': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Common.Company': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Common.SalesTaxID': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Address.Street': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Address.PostalCode': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Address.City': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  'Address.Country': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
}

const STORAGE_KEY = 'master-data-company-codes-visible-columns'
const TOGGLEABLE_FIELDS = [
  'IsStandard',
  'Common.Company',
  'Common.SalesTaxID',
  'Address.Street',
  'Address.PostalCode',
  'Address.City',
  'Address.Country',
]

export const CompanyCodes: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { uiConfig: config } = useConfigContext()
  const { navigationColumnHeader, show } = useColumnToggle(
    config.selfservices.masterDataManagement.companyCodes.table,
    TOGGLEABLE_FIELDS,
    STORAGE_KEY,
    true,
  )
  const { data: companyCodes, isLoading, error } = useAllCompanyCodes()
  const [searchParam, setSearchParam] = useSearchParam('search')

  const [selectedItems, setSelectedItems] = useState<CompanyCode[] | null>(null)

  const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters)

  useEffect(() => {
    initFilters()
  }, [])

  useEffect(() => {
    // Reset selection when data changes (e.g., after deletion)
    setSelectedItems(null)
  }, [companyCodes])

  const clearFilter = () => {
    initFilters()
  }

  const initFilters = () => {
    setFilters(defaultFilters)
    setSearchParam('')
  }

  useEffect(() => {
    onGlobalFilterChange(searchParam)
  }, [searchParam])

  const onGlobalFilterChange = (value: string) => {
    let _filters = JSON.parse(JSON.stringify(filters))

    _filters['global'].value = value
    setFilters(_filters)
    setSearchParam(value)
  }

  const handleAdd = () => navigate('add/')

  const headerActions = (
    <>
      <MasterDataDownloadButton
        label={t('masterDataManagement.companyCodes.table.download.label')}
        downloadFunction={requestDownloadCompanyCode}
        fileNamePrefix="company_codes"
      />

      <MasterDataUpload />

      <Button
        label={t('masterDataManagement.companyCodes.table.delete.label')}
        onClick={() =>
          handleDelete(
            selectedItems
              ?.map((item: CompanyCode) => item.Id)
              .filter((id): id is string => !!id) || [],
          )
        }
        icon="pi pi-trash"
        outlined
        disabled={!selectedItems?.length}
      />

      <Button
        label={t('masterDataManagement.companyCodes.table.dialog.button.add')}
        onClick={() => handleAdd()}
        icon="pi pi-plus"
        visible={
          config.selfservices.masterDataManagement.companyCodes.add.enabled !==
          false
        }
      />

      <SearchField
        searchValue={searchParam}
        setSearchValue={setSearchParam}
        placeholder={t(
          'masterDataManagement.companyCodes.table.search.placeholder',
        )}
        classNameInput="surface-50"
      />
    </>
  )

  //  action template
  const handleEdit = (companyCodeId: string) =>
    navigate('edit/' + companyCodeId)
  const handleInfo = (companyCodeId: string) =>
    navigate('info/' + companyCodeId)
  const handleDelete = (companyCodeIds: string[]) =>
    navigate('delete/' + companyCodeIds.join(','))

  const actionBodyTemplate = (rowData: CompanyCode) => {
    const isStandard = rowData.IsStandard

    return (
      <div className="flex justify-content-end flex-nowrap">
        <Button
          text
          icon="pi pi-trash"
          severity="danger"
          rounded
          aria-label={t('masterDataManagement.companyCodes.table.delete.label')}
          onClick={() => handleDelete([rowData.Id!])}
          tooltip={t(
            'masterDataManagement.companyCodes.table.delete.tooltip.isStandard',
          )}
          tooltipOptions={{
            disabled: !isStandard,
            showOnDisabled: true,
            position: 'left',
          }}
          disabled={isStandard}
        />

        <Button
          text
          icon="pi pi-info-circle"
          rounded
          aria-label={t(
            'masterDataManagement.companyCodes.table.dialog.button.info',
          )}
          onClick={() => handleInfo(rowData.Id!)}
          visible={
            config.selfservices.masterDataManagement.companyCodes.info
              .enabled !== false
          }
        />
        <Button
          text
          icon="pi pi-pencil"
          rounded
          aria-label={t(
            'masterDataManagement.companyCodes.table.dialog.button.edit',
          )}
          onClick={() => handleEdit(rowData.Id!)}
          visible={
            config.selfservices.masterDataManagement.companyCodes.edit
              .enabled !== false
          }
        />
      </div>
    )
  }

  const buttons = (
    <Column
      key="buttons"
      body={actionBodyTemplate}
      align="right"
      header={
        <div className="flex justify-content-end">{navigationColumnHeader}</div>
      }
    />
  )

  const columns: JSX.Element[] = [
    show('IsStandard') ? (
      <Column
        key="IsStandard"
        field="IsStandard"
        body={(rowData: CompanyCode) => BooleanBodyTemplate(rowData.IsStandard)}
        filter
        filterElement={BooleanFilterTemplate}
        sortable
        align="center"
      />
    ) : null,
    show('Common.DisplayId') ? (
      <Column key="Common.DisplayId" field="Common.DisplayId" filter sortable />
    ) : null,
    show('Common.Company') ? (
      <Column key="Common.Company" field="Common.Company" filter sortable />
    ) : null,
    show('Common.SalesTaxID') ? (
      <Column
        key="Common.SalesTaxID"
        field="Common.SalesTaxID"
        filter
        sortable
      />
    ) : null,
    show('Address.Street') ? (
      <Column key="Address.Street" field="Address.Street" />
    ) : null,
    show('Address.PostalCode') ? (
      <Column
        key="Address.PostalCode"
        field="Address.PostalCode"
        filter
        sortable
      />
    ) : null,
    show('Address.City') ? (
      <Column key="Address.City" field="Address.City" />
    ) : null,
    show('Address.Country') ? (
      <Column key="Address.Country" field="Address.Country" filter sortable />
    ) : null,
  ].filter((c): c is JSX.Element => c !== null)

  return (
    <>
      <ConfigurableTable
        config={config.selfservices.masterDataManagement.companyCodes.table}
        data={companyCodes}
        headerActions={headerActions}
        defaultFilters={filters}
        updateDefaultFilters={setFilters}
        clearDefaultFilter={clearFilter}
        globalFilterFields={[
          'Common.DisplayId',
          'Common.Company',
          'Common.SalesTaxID',
        ]}
        globalFilterValue={searchParam}
        setGlobalFilterValue={setSearchParam}
        isLoading={isLoading}
        stateKey="table-master-data-management-company-codes"
        buttons={buttons}
        checkboxen
        selected={selectedItems}
        setSelected={setSelectedItems}
      >
        {columns}
      </ConfigurableTable>

      <Outlet />
    </>
  )
}
